import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import styles from '../css/comuns/Ofertas.module.css';
import { config } from '../../data';

// Componente Ofertas: Exibe um carrossel de ofertas com funcionalidades de navegação e expansão de conteúdo.
const Ofertas = () => {
    // useState para controlar o índice do card atual no carrossel
    const [currentIndex, setCurrentIndex] = useState(0);
    // useState para controlar se todos os cards estão abertos ou fechados
    const [allCardsOpen, setAllCardsOpen] = useState(false);
    // useState para controlar o estado de abertura de cada card individualmente
    const [openCards, setOpenCards] = useState({});
    // useState para controlar a quantidade de cards visíveis no carrossel, adaptável ao tamanho da tela
    const [visibleCards, setVisibleCards] = useState(4);

    // Utiliza os dados de ofertas do config
    const cards = config.content.ofertas;

    // useEffect para ajustar a quantidade de cards visíveis conforme o tamanho da tela
    useEffect(() => {
        // Função para verificar e atualizar a quantidade de cards visíveis
        const handleResize = () => {
            // Define se é um dispositivo móvel baseado na largura da tela
            const isMobile = window.innerWidth <= 768;
            // Atualiza a quantidade de cards visíveis
            setVisibleCards(isMobile ? 1 : 4);
            // Ajusta o índice atual para não ultrapassar o limite de cards visíveis
            setCurrentIndex(prev => Math.min(prev, cards.length - (isMobile ? 1 : 4)));
        };
        // Executa a função handleResize na montagem do componente
        handleResize();
        // Adiciona um listener de evento para o redimensionamento da tela
        window.addEventListener('resize', handleResize);
        // Remove o listener de evento na desmontagem do componente
        return () => window.removeEventListener('resize', handleResize);
    }, [cards.length]); // Dependência no tamanho do array de cards

    // Função para avançar para o próximo card no carrossel
    const nextCard = () => {
        setCurrentIndex(prev => Math.min(prev + 1, cards.length - visibleCards));
    };

    // Função para voltar para o card anterior no carrossel
    const prevCard = () => {
        setCurrentIndex(prev => Math.max(prev - 1, 0));
    };

    // Função para abrir ou fechar todos os cards simultaneamente
    const toggleAllCards = () => {
        // Cria um novo objeto para armazenar o estado de abertura de cada card
        const newOpenCards = {};
        // Itera sobre cada card para definir o estado de abertura como o inverso do estado atual (allCardsOpen)
        cards.forEach(card => {
            newOpenCards[card.id] = !allCardsOpen;
        });
        // Atualiza o estado openCards com o novo objeto
        setOpenCards(newOpenCards);
        // Inverte o estado de allCardsOpen
        setAllCardsOpen(!allCardsOpen);
    };

    // Função para alternar a visibilidade do conteúdo de um card específico
    const toggleCardContent = (cardId) => {
        // Atualiza o estado openCards para o cardId especificado
        setOpenCards(prev => ({
            ...prev,
            [cardId]: !prev[cardId] // Inverte o estado de aberto/fechado do card
        }));
    };

    // Renderização do componente
    return (
        <div className={styles['offer-carousel']}>
            {/* Container para o título e descrição das ofertas */}
            <div className={styles['offer-headline-container']}>
                <div className={styles['offer-headline']}>
                    <h2>Turbine sua conexão</h2>
                    <p>Planos de internet ultrarrápida com benefícios exclusivos para sua empresa!</p>
                </div>
            </div>
            {/* Container para o toggle de abrir/fechar todos os cards */}
            <div className={styles['toggle-all-container']}>
                <label className={styles['toggle-all-switch']}>
                    <input type="checkbox" checked={allCardsOpen} onChange={toggleAllCards} />
                    <span className={`${styles.slider} ${styles.round}`}></span>
                </label>
                <span className={styles['toggle-all-text']}>
                    {allCardsOpen ? 'Fechar todos os cards' : 'Abrir todos os cards'}
                </span>
            </div>
            {/* Container principal do carrossel */}
            <div className={styles['carousel-container']}>
                {/* Botão de navegação para o card anterior */}
                <button
                    className={`${styles['nav-button']} ${styles.prev}`}
                    onClick={prevCard}
                    disabled={currentIndex === 0}
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                {/* Botão de navegação para o próximo card */}
                <button
                    className={`${styles['nav-button']} ${styles.next}`}
                    onClick={nextCard}
                    disabled={currentIndex === cards.length - visibleCards}
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
                {/* Container dos cards, com efeito de translação para o carrossel */}
                <div 
                    className={styles['card-container']} 
                    style={{ transform: `translateX(-${currentIndex * (100 / visibleCards)}%)` }}
                >
                    {/* Mapeamento dos cards */}
                    {cards.map((card, index) => (
                        <div 
                            key={card.id} 
                            className={`${styles['card-oferta']} ${
                                index >= currentIndex && index < currentIndex + visibleCards 
                                    ? styles.visible 
                                    : styles.hidden
                            }`} 
                            id={`card-oferta-${card.id}`}
                        >
                            {/* Selo do card, exibido se existir */}
                            {card.seal && <img src={card.seal} alt="Selo" className={styles.selo} />}
                            {/* Header do card */}
                            <div className={styles.header}>{card.header}</div>
                            {/* Conteúdo principal do card */}
                            <div className={styles['card-content']}>
                                {/* Informações de velocidade */}
                                <div>
                                    <div className={styles.speed}>{card.speed}</div>
                                    <div className={styles['speed-subtext']}>MEGA</div>
                                </div>
                                {/* Toggle para exibir/ocultar os benefícios do card */}
                                <div
                                    className={styles['toggle-benefits']}
                                    onClick={() => toggleCardContent(card.id)}
                                >
                                    <span className={styles.icon}>{openCards[card.id] ? '-' : '+'}</span>
                                    <span>{openCards[card.id] ? 'Ocultar Benefícios' : 'Ver Benefícios'}</span>
                                </div>

                                {/* Divisor que aparece quando os benefícios estão ocultos */}
                                <div className={`${styles['toggle-divider']} ${openCards[card.id] ? styles.hidden : ''}`}></div>
                                {/* Conteúdo dos benefícios, exibido ou oculto conforme o estado do card */}
                                <div className={`${styles['benefits-content']} ${openCards[card.id] ? '' : styles['hidden-content']}`}>
                                    {/* Mapeamento dos benefícios */}
                                    {card.benefits.map((benefit, idx) => (
                                        <React.Fragment key={idx}>
                                            {/* Divider entre os benefícios, exceto o primeiro */}
                                            {idx !== 0 && <div className={styles.divider}></div>}
                                            {/* Renderização condicional para benefícios do tipo "sideBySide" */}
                                            {benefit.type === "sideBySide" ? (
                                                <div className={`${styles.partners} ${styles.sideBySide}`}>
                                                    {benefit.images.map((image, imageIdx) => (
                                                        <img key={imageIdx} src={image.img} alt={image.alt} />
                                                    ))}
                                                </div>
                                            ) : (
                                                /* Renderização para outros tipos de benefícios */
                                                <div className={`${styles.partners} ${styles[card.cardType]}`}>
                                                    <img
                                                        src={benefit.img}
                                                        alt={benefit.alt}
                                                        style={{
                                                            width: benefit.width ? `${benefit.width}px` : '',
                                                            marginTop: benefit.marginTop ? `${benefit.marginTop}px` : '',
                                                            marginBottom: benefit.marginBottom ? `${benefit.marginBottom}px` : ''
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </React.Fragment>
                                    ))}
                                    {/* Divider após os benefícios */}
                                    <div className={styles.divider}></div>
                                    {/* Escolhas de aplicativos */}
                                    <div className={styles['app-choices']}>OU 1 APP STANDARD DE SUA ESCOLHA:</div>
                                    {/* Ícones dos aplicativos */}
                                    <div className={styles['app-icons']}>
                                        {card.appChoices.map((app, idx) => (
                                            <img key={idx} src={app.img} alt={app.alt} />
                                        ))}
                                    </div>
                                </div>
                                {/* Texto de preço */}
                                <div className={`${styles['price-text']} ${openCards[card.id] ? '' : styles['hidden-content']}`}>
                                    Tudo isso por
                                </div>
                                {/* Preço do plano */}
                                <div className={styles.price}>
                                    <span className={styles.currency}>R$</span>
                                    <span className={styles.integer}>{Math.floor(card.price)}</span>
                                    <div>
                                        <span className={styles.decimal}>,{(card.price % 1).toFixed(2).slice(2)}</span>
                                        <span className={styles.month}>/mês</span>
                                    </div>
                                </div>
                                {/* Botão de contratação */}
                                <button className={styles['btn-contratar']}>Contrate Agora!</button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Ofertas;